/**
 * Leads Dashboard — admin-only management of leads captured via public landings.
 * Route: /settings/leads
 *
 * - KPI cards (total, by source, by level, HubSpot sync)
 * - Filters (source, level, search, date range)
 * - Table with actions (send demo email, sync to HubSpot, delete)
 * - Export CSV + signed PDF (SHA-256)
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Users, Sparkles, TrendingUp, Filter, Download, FileText, Send, RefreshCw,
  Trash2, ExternalLink, Loader2, Mail, Building2, ShieldCheck, ShieldAlert, Award, Copy
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  public_landing_ai_act: { label: 'AI Act', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  public_landing_nis2: { label: 'NIS2', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  public_trust_center: { label: 'Trust', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const LEVEL_LABEL = {
  critical: { label: 'Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium: { label: 'Moderado', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function LeadsDashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source: 'all', level: 'all', q: '' });
  const [busy, setBusy] = useState({}); // {lead_id: 'demo'|'hubspot'|'delete'|'badge'}
  const [exporting, setExporting] = useState(false);
  const [badgeInfo, setBadgeInfo] = useState(null); // {lead, snippets, verify_url, qr_url}

  useEffect(() => { document.title = 'Leads · NIS2AI'; }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.level !== 'all') params.level = filters.level;
      if (filters.q.trim()) params.q = filters.q.trim();
      params.limit = 200;
      const [statsRes, listRes] = await Promise.all([
        api.get('/leads/stats'),
        api.get('/leads', { params }),
      ]);
      setStats(statsRes.data);
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
    } catch (err) {
      toast.error('Falha ao carregar leads.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (filters.source !== 'all') params.set('source', filters.source);
      if (filters.level !== 'all') params.set('level', filters.level);
      if (filters.q.trim()) params.set('q', filters.q.trim());
      const token = localStorage.getItem('auth_token');
      const url = `${BACKEND_URL}/api/leads/export.${format}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const hash = res.headers.get('X-Content-Hash-SHA256');
      const total = res.headers.get('X-Total-Records');
      const filename = `nis2ai_leads_${new Date().toISOString().slice(0, 10)}.${format}`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      if (format === 'pdf' && hash) {
        toast.success(`${total} leads exportados · SHA-256: ${hash.slice(0, 10)}…`);
      } else {
        toast.success(`${total} leads exportados em ${format.toUpperCase()}.`);
      }
    } catch (err) {
      toast.error('Falha ao exportar.');
    } finally {
      setExporting(false);
    }
  };

  const sendDemo = async (lead) => {
    setBusy(b => ({ ...b, [lead.lead_id]: 'demo' }));
    try {
      await api.post(`/leads/${lead.lead_id}/send-demo`);
      toast.success(`Email de demo enviado para ${lead.email}.`);
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao enviar email.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[lead.lead_id]; return n; });
    }
  };

  const syncHubSpot = async (lead) => {
    setBusy(b => ({ ...b, [lead.lead_id]: 'hubspot' }));
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/sync-hubspot`);
      toast.success(`HubSpot: ${data.action} · ${lead.email}.`);
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao sincronizar com HubSpot.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[lead.lead_id]; return n; });
    }
  };

  const deleteLead = async (lead) => {
    setBusy(b => ({ ...b, [lead.lead_id]: 'delete' }));
    try {
      await api.delete(`/leads/${lead.lead_id}`);
      toast.success('Lead removido.');
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao remover.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[lead.lead_id]; return n; });
    }
  };

  const openBadge = async (lead) => {
    setBusy(b => ({ ...b, [lead.lead_id]: 'badge' }));
    try {
      const { data } = await api.get(`/leads/${lead.lead_id}/badge`);
      setBadgeInfo({ lead, ...data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao gerar badge.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[lead.lead_id]; return n; });
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado.`);
    } catch (err) {
      toast.error('Falha ao copiar.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="leads-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" /> Leads capturados
          </h1>
          <p className="text-sm text-slate-500 mt-1">Diagnósticos submetidos via landings públicas <span className="font-medium">/landing/ai-act</span> e <span className="font-medium">/landing/nis2</span>.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => handleExport('csv')}
            disabled={!!exporting}
            data-testid="leads-export-csv-btn"
          >
            {exporting === 'csv' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />} CSV
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => handleExport('pdf')}
            disabled={!!exporting}
            data-testid="leads-export-pdf-btn"
            title="PDF assinado com hash SHA-256"
          >
            {exporting === 'pdf' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />} PDF Assinado
          </Button>
          <Button variant="ghost" size="sm" onClick={loadData} data-testid="leads-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Users} label="Total leads" value={stats?.total ?? '—'}
          hint={stats ? `${Object.keys(stats.by_source || {}).length} fontes` : ''}
          testid="kpi-total"
        />
        <KpiCard
          icon={Sparkles} label="AI Act" value={stats?.by_source?.public_landing_ai_act?.count ?? 0}
          hint={stats ? `score médio ${stats.by_source?.public_landing_ai_act?.avg_score ?? 0}` : ''}
          tone="indigo" testid="kpi-aiact"
        />
        <KpiCard
          icon={ShieldCheck} label="NIS2" value={stats?.by_source?.public_landing_nis2?.count ?? 0}
          hint={stats ? `score médio ${stats.by_source?.public_landing_nis2?.avg_score ?? 0}` : ''}
          tone="emerald" testid="kpi-nis2"
        />
        <KpiCard
          icon={TrendingUp} label="Sincronizados HubSpot"
          value={stats?.hubspot?.synced ?? 0}
          hint={stats?.hubspot?.errors > 0 ? `${stats.hubspot.errors} com erro` : 'ok'}
          tone={stats?.hubspot?.errors > 0 ? 'red' : 'slate'}
          testid="kpi-hubspot"
        />
      </div>

      {/* Level breakdown mini bar */}
      {stats?.by_level && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Distribuição por nível de exposição</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3 flex-wrap">
            {['critical', 'high', 'medium', 'low'].map(lvl => (
              <div key={lvl} className="flex items-center gap-2" data-testid={`kpi-level-${lvl}`}>
                <Badge variant="outline" className={cn('text-[11px]', LEVEL_LABEL[lvl].tone)}>{LEVEL_LABEL[lvl].label}</Badge>
                <span className="text-lg font-semibold text-slate-900">{stats.by_level[lvl] || 0}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Select value={filters.source} onValueChange={v => setFilters({ ...filters, source: v })}>
              <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Fonte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as fontes</SelectItem>
                <SelectItem value="public_landing_ai_act">AI Act</SelectItem>
                <SelectItem value="public_landing_nis2">NIS2</SelectItem>
                <SelectItem value="public_trust_center">Trust Center</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={filters.level} onValueChange={v => setFilters({ ...filters, level: v })}>
              <SelectTrigger data-testid="filter-level"><SelectValue placeholder="Nível" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os níveis</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
                <SelectItem value="high">Alto</SelectItem>
                <SelectItem value="medium">Moderado</SelectItem>
                <SelectItem value="low">Baixo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Input
              placeholder="Pesquisar por email, nome ou empresa…"
              value={filters.q}
              onChange={e => setFilters({ ...filters, q: e.target.value })}
              data-testid="filter-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700">
            {loading ? 'A carregar…' : `${items.length} de ${total} leads`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> A carregar leads…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Nenhum lead com estes filtros.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3">Data (UTC)</th>
                    <th className="px-4 py-3">Fonte</th>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-4 py-3">Empresa</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Nível</th>
                    <th className="px-4 py-3">HubSpot</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(lead => {
                    const src = SOURCE_LABEL[lead.source] || { label: lead.source, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                    const lvl = LEVEL_LABEL[lead.level] || { label: lead.level, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                    const hs = lead.hubspot;
                    const isBusy = busy[lead.lead_id];
                    return (
                      <tr key={lead.lead_id} className="hover:bg-slate-50" data-testid={`lead-row-${lead.lead_id}`}>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{(lead.created_at || '').replace('T', ' ').slice(0, 16)}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className={cn('text-[11px]', src.tone)}>{src.label}</Badge></td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{lead.name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-slate-400" />{lead.company}</div>
                          {lead.role && <div className="text-xs text-slate-500">{lead.role}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono font-semibold">{lead.score}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className={cn('text-[11px]', lvl.tone)}>{lvl.label}</Badge></td>
                        <td className="px-4 py-3">
                          {hs?.status === 'ok' ? (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" title={`${hs.action} · ${hs.contact_id}`}>
                              ✓ {hs.action}
                            </Badge>
                          ) : hs?.status === 'error' ? (
                            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" title={hs.message}>
                              erro
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => sendDemo(lead)}
                              disabled={!!isBusy}
                              title="Enviar convite de demo (Resend)"
                              data-testid={`lead-send-demo-${lead.lead_id}`}
                            >
                              {isBusy === 'demo' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => openBadge(lead)}
                              disabled={!!isBusy}
                              title="Gerar badge NIS2AI Verified"
                              data-testid={`lead-badge-${lead.lead_id}`}
                            >
                              {isBusy === 'badge' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Award className="h-3.5 w-3.5 text-emerald-600" />}
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => syncHubSpot(lead)}
                              disabled={!!isBusy}
                              title="Sincronizar com HubSpot"
                              data-testid={`lead-sync-hs-${lead.lead_id}`}
                            >
                              {isBusy === 'hubspot' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" disabled={!!isBusy} title="Remover" data-testid={`lead-delete-${lead.lead_id}`}>
                                  {isBusy === 'delete' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-500" />}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover este lead?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Vai apagar permanentemente <b>{lead.email}</b>. Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid={`lead-delete-cancel-${lead.lead_id}`}>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteLead(lead)} className="bg-red-600 hover:bg-red-700" data-testid={`lead-delete-confirm-${lead.lead_id}`}>
                                    Remover
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verified Badge dialog */}
      <Dialog open={!!badgeInfo} onOpenChange={(o) => !o && setBadgeInfo(null)}>
        <DialogContent className="max-w-2xl" data-testid="badge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-emerald-600" />
              NIS2AI Verified · {badgeInfo?.lead?.company}
            </DialogTitle>
            <DialogDescription>
              Copia um dos snippets abaixo e cola no website do cliente. A URL é assinada HMAC — qualquer alteração aos parâmetros invalida a certificação.
            </DialogDescription>
          </DialogHeader>

          {badgeInfo && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <img src={badgeInfo.qr_url} alt="QR code" className="h-24 w-24 rounded-lg bg-white p-1 border border-slate-200" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Verify URL</p>
                    <a href={badgeInfo.verify_url} target="_blank" rel="noreferrer" className="text-sm text-slate-800 break-all font-mono hover:text-indigo-700" data-testid="badge-verify-url">
                      {badgeInfo.verify_url}
                    </a>
                    <div className="mt-2 flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(badgeInfo.verify_url, 'URL')} data-testid="badge-copy-url">
                        <Copy className="h-3 w-3 mr-1" /> Copiar URL
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(badgeInfo.verify_url, '_blank')}>
                        <ExternalLink className="h-3 w-3 mr-1" /> Pré-visualizar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <Tabs defaultValue="html">
                <TabsList>
                  <TabsTrigger value="html" data-testid="badge-tab-html">HTML</TabsTrigger>
                  <TabsTrigger value="markdown" data-testid="badge-tab-markdown">Markdown</TabsTrigger>
                  <TabsTrigger value="iframe" data-testid="badge-tab-iframe">iFrame</TabsTrigger>
                </TabsList>
                {['html', 'markdown', 'iframe'].map(kind => (
                  <TabsContent key={kind} value={kind}>
                    <div className="relative">
                      <pre className="p-3 bg-slate-900 text-slate-100 text-xs rounded-lg overflow-x-auto whitespace-pre-wrap break-all max-h-64" data-testid={`badge-snippet-${kind}`}>
                        {badgeInfo.snippets[kind]}
                      </pre>
                      <Button
                        size="sm" variant="secondary"
                        onClick={() => copyToClipboard(badgeInfo.snippets[kind], kind.toUpperCase())}
                        className="absolute top-2 right-2"
                        data-testid={`badge-copy-${kind}`}
                      >
                        <Copy className="h-3 w-3 mr-1" /> Copiar
                      </Button>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <p className="text-xs text-slate-500">
                Assinatura HMAC-SHA256 · válida indefinidamente · pode ser revogada apagando o lead.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone = 'slate', testid }) {
  const toneClasses = {
    indigo: 'from-indigo-500 to-indigo-600 text-indigo-100',
    emerald: 'from-emerald-500 to-emerald-600 text-emerald-100',
    red: 'from-red-500 to-red-600 text-red-100',
    slate: 'from-slate-700 to-slate-800 text-slate-200',
  }[tone];
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
          </div>
          <div className={cn('h-10 w-10 rounded-xl bg-gradient-to-br flex items-center justify-center', toneClasses)}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
